import { useCallback, useMemo } from 'react';
import { toast } from 'sonner';
import api, { formatApiError } from '../../lib/api';

export function useOrderActions({ state, setState, refresh }) {
  const placeOrder = useCallback(async ({ address, paymentMethod, voucher, referral, buyerKey, orderType }) => {
    const selected = state.cart.filter((c) => c.selected);
    if (!selected.length) return [];
    const items = selected.map((c) => ({ productId: c.productId, qty: c.qty, variant: c.variant || '' }));
    try {
      const res = await api.post('/orders', {
        address, paymentMethod, orderType: orderType || 'ORDER',
        voucherCode: voucher?.code || '', referralCode: referral?.code || '', items,
      });
      const usedIds = new Set(selected.map((c) => `${c.productId}|${c.variant}`));
      setState((s) => ({ ...s, cart: s.cart.filter((c) => !usedIds.has(`${c.productId}|${c.variant}`)) }));
      localStorage.removeItem('kelate_voucher');
      await refresh(false);
      return res.data;
    } catch (e) {
      const msg = formatApiError(e);
      if (msg.toLowerCase().includes('insufficient wallet')) {
        return { error: 'insufficient-wallet', balance: state.wallets[buyerKey] || 0 };
      }
      toast.error(msg);
      return { error: 'failed' };
    }
  }, [state, refresh]);

  const placeAppointment = useCallback(async ({ merchant, customerName, customerPhone, note }) => {
    try {
      const services = state.customProducts.filter((p) => p.merchantId === merchant.id);
      const items = (services.length ? services.slice(0, 1) : [{ id: null, name: merchant.name }]).map((p) => ({ productId: p.id, qty: 1, variant: note ? note.slice(0, 80) : '' })).filter((i) => i.productId);
      if (!items.length) return { error: 'failed' };
      const res = await api.post('/orders', {
        orderType: 'APPOINTMENT', paymentMethod: 'CASH',
        address: { name: customerName, phone: customerPhone, line1: note || 'Appointment', district: '', state: 'Kelantan' },
        voucherCode: '', referralCode: '', items,
      });
      await refresh(false);
      return res.data;
    } catch (e) {
      toast.error(formatApiError(e));
      return { error: 'failed' };
    }
  }, [state.customProducts, refresh]);

  const merchantOrderAction = useCallback(async (orderId, action) => {
    try {
      await api.post(`/merchant/orders/${orderId}/action`, { action });
      await refresh(false);
    } catch (e) { toast.error(formatApiError(e)); }
  }, [refresh]);

  const assignRider = useCallback(async (deliveryId, riderId) => {
    try {
      await api.post(`/admin/deliveries/${deliveryId}/assign`, { riderId });
      await refresh(false);
    } catch (e) { toast.error(formatApiError(e)); }
  }, [refresh]);

  const riderAction = useCallback(async (deliveryId, action, payload = {}) => {
    try {
      await api.post(`/rider/deliveries/${deliveryId}/action`, { action, ...payload });
      await refresh(false);
      return { ok: true };
    } catch (e) {
      const msg = formatApiError(e);
      if (msg === 'payment-not-collected') return { error: 'payment-not-collected' };
      toast.error(msg);
      return { error: msg };
    }
  }, [refresh]);

  const adminAssignRiderToOrder = useCallback(async (orderId, riderId) => {
    try {
      await api.post(`/admin/orders/${orderId}/assign`, { riderId });
      await refresh(false);
      return { ok: true };
    } catch (e) { toast.error(formatApiError(e)); return { error: true }; }
  }, [refresh]);

  const verifyDigitalCollection = useCallback(async (entryId, action) => {
    try {
      await api.post(`/admin/finance/digital/${entryId}/verify`, { action });
      toast.success(action === 'verify' ? 'Collection confirmed' : 'Flagged as discrepancy');
      await refresh(false);
    } catch (e) { toast.error(formatApiError(e)); }
  }, [refresh]);

  return useMemo(() => ({ placeOrder, placeAppointment, merchantOrderAction, assignRider, adminAssignRiderToOrder, riderAction, verifyDigitalCollection }),
    [placeOrder, placeAppointment, merchantOrderAction, assignRider, adminAssignRiderToOrder, riderAction, verifyDigitalCollection]);
}
